/**
 * Область просмотра Диска — чистые функции, определяющие «докуда видно».
 *
 * Экраны Диска работают либо со всем Диском (страница, файл-пикер), либо с
 * одним поддеревом (встраиваемая панель сущности). Отличие сводится к двум
 * операциям — найти корень области и обрезать по нему путь — и обе не зависят
 * ни от React, ни от сети, поэтому живут отдельно и покрыты юнитами.
 *
 * Сосед: `folderSubtree.ts` — какие папки попадают в область.
 */
import type { Folder } from '../../../schemas/folder.schema';
import type { DriveRow } from './driveRow';

/**
 * Путь от корня Диска до папки — подъём по `parent_id` в плоском списке.
 *
 * Повторно посещённые узлы отсекаются: битые данные с циклом в `parent_id`
 * дают конечный путь, а не вечный цикл. Отсутствующая папка — пустой путь.
 *
 * @param foldersById папки по id (обычно весь видимый Диск)
 * @param folderId папка, до которой строим путь
 * @returns сегменты от корня к папке включительно
 */
export function buildFolderPath(foldersById: Map<number, Folder>, folderId: number): Folder[] {
  const path: Folder[] = [];
  const visited = new Set<number>();
  let cursor = foldersById.get(folderId);
  while (cursor && !visited.has(cursor.id)) {
    visited.add(cursor.id);
    path.unshift(cursor);
    cursor = cursor.parent_id != null ? foldersById.get(cursor.parent_id) : undefined;
  }
  return path;
}

/**
 * Папка-корень области по системному якорю (`folders.code`).
 *
 * Якорь, а не id: юзер волен переименовать и перенести папку, `code` это
 * переживает (его проставляют `DriveService.ensureFolderPath` и правила
 * `defineFilePlacement`). Вызывающая сторона знает id своей сущности, а не id
 * папки, поэтому связь идёт через код вида `mws:project:42`.
 *
 * @param folders плоский список видимых папок
 * @param code якорь искомой папки; пустой/`null` — поиск не выполняется
 * @returns папка либо `null`, если её нет или она не видна текущему юзеру
 */
/**
 * Ближайшая якорная папка области: сама папка (если у неё есть code) или
 * первый предок с непустым code. NULL — якоря в цепочке нет. Зеркало
 * серверного nearestAnchorFolder для доменных действий меню.
 */
export function nearestAnchorRow(folder: Folder, foldersById: Map<number, Folder>): Folder | null {
  let current: Folder | undefined = folder;
  const seen = new Set<number>();
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    if (current.code != null && current.code !== '') return current;
    current = current.parent_id != null ? foldersById.get(current.parent_id) : undefined;
  }
  return null;
}

export function resolveRootByCode(
  folders: Folder[],
  code: string | null | undefined
): Folder | null {
  const anchor = (code ?? '').trim();
  if (!anchor) return null;
  return folders.find(folder => folder.code === anchor) ?? null;
}

/**
 * Обрезает путь-крошки по корню области: всё выше корня отбрасывается.
 *
 * Без обрезки встраиваемая панель показывала бы полный путь от «Общего диска»,
 * и по крошкам можно было бы уйти за пределы папки сущности — то есть увидеть
 * чужие файлы там, где обещали показать только свои.
 *
 * Если корня в пути нет (данные разъехались: папку перенесли за пределы
 * области), путь возвращается целиком — лучше показать больше контекста, чем
 * пустые крошки; ограничение выборки всё равно держит `filterByFolder`.
 *
 * @param path полный путь от корня Диска (`buildFolderPath`)
 * @param rootId id корня области; `null`/`undefined` — обрезка не нужна
 * @returns путь, начинающийся с корня области
 */
export function clampPathToRoot(path: Folder[], rootId: number | null | undefined): Folder[] {
  if (rootId == null) return path;
  const rootIndex = path.findIndex(segment => segment.id === rootId);
  return rootIndex >= 0 ? path.slice(rootIndex) : path;
}

/**
 * Когда показывать путь к папке под именем записи:
 * - `never` — никогда (список одной папки: путь известен из крошек);
 * - `foreign` — только у записей из ДРУГИХ папок, т.е. в результатах
 *   глобального поиска на странице Диска;
 * - `always` — всегда (модалка поиска, где папка у каждой строки своя).
 */
export type DriveLocationMode = 'never' | 'foreign' | 'always';

export interface ResolveEntryLocationOptions {
  foldersById: Map<number, Folder>;
  mode?: DriveLocationMode;
  /** Активная папка — нужна режиму `foreign`, чтобы понять, «чужая» ли строка. */
  activeFolderId?: number | null;
}

/**
 * Путь к папке записи для показа под её именем («Проекты / Проект А»).
 *
 * Правило «что считается чужой папкой» живёт здесь, а не в ячейке таблицы:
 * оно одинаково для всех экранов и проверяется юнитом.
 *
 * @returns путь через « / » либо пустая строка, если показывать не нужно
 */
export function resolveEntryLocation(
  row: DriveRow,
  { foldersById, mode = 'never', activeFolderId }: ResolveEntryLocationOptions
): string {
  if (mode === 'never') return '';
  const parentId = row.entry_parent_id;
  if (parentId == null) return '';
  if (mode === 'foreign' && parentId === activeFolderId) return '';
  return buildFolderPath(foldersById, parentId)
    .map(segment => segment.name)
    .join(' / ');
}
